import { InfiniteData, useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query';

import { ClipCreatorParam, fetchClips, GetClipsResult } from './useAllClipsQuery';
import { ClipStatus } from '../types';

/**
 * Small enough that the first screen paints quickly, large enough that a full
 * grid usually arrives in one request. The sentinel fetches the rest.
 */
export const CLIP_LIBRARY_PAGE_SIZE = 60;

/**
 * Everything the library filters on. All of it is resolved server-side, so a
 * change here is a new query rather than a re-filter of what is already loaded
 * — which is the point: the grid is no longer capped at whatever one page held.
 *
 * `undefined` means "no filter"; there is no 'all' sentinel to encode.
 */
export type ClipLibraryFilters = {
  leagueId?: number;
  seasonId?: number;
  gameId?: number;
  /** SES tracked-object id, not our player.id. */
  playerId?: number;
  eventType?: string;
  creator?: ClipCreatorParam;
  /** Free text. Debounce before passing it in — every change refetches. */
  text?: string;
  /**
   * Leaves rule-cut clips out entirely. Set while the zeroTouchClipping flag is
   * off, where automated clips are not the viewer's to see at all — so it is a
   * gate rather than one of the filters above.
   */
  excludeAutomated?: boolean;
};

const useClipLibraryQuery = (
  filters: ClipLibraryFilters,
): UseInfiniteQueryResult<InfiniteData<GetClipsResult>, Error> =>
  useInfiniteQuery({
    // The whole filter object is the key, so any change starts a fresh
    // paginated sequence instead of appending to the previous one.
    queryKey: ['clips', 'library', CLIP_LIBRARY_PAGE_SIZE, filters],
    queryFn: ({ pageParam }) =>
      fetchClips({
        page: pageParam,
        pageSize: CLIP_LIBRARY_PAGE_SIZE,
        sort: 'desc',
        sortField: 'createdAt',
        clipType: 'clip',
        // The library has no greyed-out state for soft-deleted clips the way the
        // clips table does, so a deleted clip should simply not be listed.
        deleted: false,
        // Same reasoning for anything that is not a finished render: the
        // library is a browsable grid of watchable clips, and a card whose
        // export failed or has not run yet has no preview to show and nothing
        // to play. The clips table is where a non-READY export is inspected.
        status: ClipStatus.READY,
        textSearch: filters.text,
        leagueId: filters.leagueId,
        seasonId: filters.seasonId,
        gameId: filters.gameId,
        playerId: filters.playerId,
        eventType: filters.eventType,
        creator: filters.creator,
        excludeAutomated: filters.excludeAutomated,
      }),
    initialPageParam: 0,
    // rowCount is the total matching the same filters, so "is there more" is a
    // comparison against what has been loaded rather than a full-page guess —
    // that avoids the trailing empty request when the last page is exactly full.
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, page) => count + page.rows.length, 0);
      return loaded < lastPage.rowCount ? allPages.length : undefined;
    },
    // Hold the previous result while a filter change is in flight, so the grid
    // dims instead of collapsing to an empty state and back.
    placeholderData: (previous) => previous,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 2,
    throwOnError: false,
  });

export default useClipLibraryQuery;
